/**
 * NotificationSystem — global site notifications.
 *
 * Two parts:
 *   1. <NotificationPoller />  — polls /api/v1/monitors/events every 30s,
 *      fires toasts when new unacked alarms arrive.
 *   2. <NotificationToasts />  — renders the toast stack (bottom-right).
 *
 * Mount both once in App.tsx.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNotificationStore } from '@/store/notificationStore';
import type { AppNotification } from '@/store/notificationStore';

// ── Poller ────────────────────────────────────────────────────────────────────

export function NotificationPoller() {
  const add = useNotificationStore((s) => s.add);
  const setUnacked = useNotificationStore((s) => s.setUnackedAlarms);
  const seenIds = useRef<Set<number>>(new Set());

  const poll = useCallback(async () => {
    try {
      const r = await fetch('/api/v1/monitors/events?limit=20');
      if (!r.ok) return;
      const raw = await r.json();
      const events = (Array.isArray(raw) ? raw : (raw.events ?? [])) as Array<{
        id: number; event_type: string; severity: string;
        summary: string; acknowledged_at: string | null;
      }>;
      const unacked = events.filter((e) => !e.acknowledged_at);
      setUnacked(unacked.length);
      for (const ev of unacked) {
        if (seenIds.current.has(ev.id)) continue;
        seenIds.current.add(ev.id);
        add({
          type: ev.severity === 'critical' ? 'alarm' : 'warning',
          title: `Monitor alarm — ${ev.event_type.replace(/_/g, ' ')}`,
          body: ev.summary,
        });
      }
    } catch {
      // silent — network blip
    }
  }, [add, setUnacked]);

  useEffect(() => {
    void poll();
    const id = setInterval(poll, 30_000);
    return () => clearInterval(id);
  }, [poll]);

  return null;
}

// ── Toasts ────────────────────────────────────────────────────────────────────

const TYPE_COLOR: Record<AppNotification['type'], string> = {
  alarm:   '#f85149',
  warning: '#d29922',
  info:    '#58a6ff',
  success: '#3fb950',
};

const TYPE_ICON: Record<AppNotification['type'], string> = {
  alarm:   '⚠',
  warning: '●',
  info:    'ℹ',
  success: '✓',
};

function Toast({ n, onDismiss }: { n: AppNotification; onDismiss: () => void }) {
  const color = TYPE_COLOR[n.type];

  // auto-dismiss after 8s
  useEffect(() => {
    const t = setTimeout(onDismiss, 8_000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div style={{
      background: '#161b22',
      border: `1px solid ${color}66`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 6,
      padding: '10px 14px',
      marginBottom: 8,
      width: 300,
      boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
      fontFamily: '"JetBrains Mono", monospace',
      animation: 'slide-in 0.2s ease',
      position: 'relative',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ color, fontSize: 14, flexShrink: 0, marginTop: 1 }}>{TYPE_ICON[n.type]}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#e6edf3', marginBottom: 3 }}>{n.title}</div>
          <div style={{ fontSize: 10, color: '#8b949e', lineHeight: 1.4, wordBreak: 'break-word' }}>{n.body}</div>
        </div>
        <button
          onClick={onDismiss}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#8b949e', fontSize: 14, padding: 0, lineHeight: 1, flexShrink: 0,
          }}
        >×</button>
      </div>
    </div>
  );
}

export function NotificationToasts() {
  const notifications = useNotificationStore((s) => s.notifications);
  const dismiss = useNotificationStore((s) => s.dismiss);
  const visible = notifications.filter((n) => !n.dismissed).slice(0, 5);

  if (visible.length === 0) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
      display: 'flex', flexDirection: 'column-reverse',
    }}>
      <style>{`@keyframes slide-in { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }`}</style>
      {visible.map((n) => (
        <Toast key={n.id} n={n} onDismiss={() => dismiss(n.id)} />
      ))}
    </div>
  );
}
